import React, { useMemo } from "react";
import { ArrowUpRight } from "@dreamhub/designsystem/icons";
import { DealData } from "@/pages/deals/types";
import { CompanyData } from "@/pages/companies/types";
import { getFullName } from "@/entities/people/helpers";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { PersonData } from "@/entities/people/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import SingleInput from "@/components/controls/SingleInput";
import { UserData } from "@/entities/users/types";
import {
  EntityType,
  useEntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { LeadData } from "@/pages/leads/types";
import { ContractData } from "@/pages/contracts/types";
import { PartnerData } from "@/pages/partners/types";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { StageBadge } from "@/components/ui/stage-badge";
import { IconButton } from "@/components/ui/icon-button";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, isEmpty } from "lodash";
import ClickableLink from "@/components/ui/clickable-link";
import { getDealDetailedViewUrl } from "@/services/hooks/entities/deal/useDealsNavigation";
import { EntityData } from "@/services/api/types";

type drawerItem =
  | DealData
  | CompanyData
  | PersonData
  | UserData
  | LeadData
  | ContractData
  | PartnerData
  | PartnerContactData
  | null;

interface QuickViewTitleProps {
  item: drawerItem;
  itemType: EntityType;
  currentEntity: EntityData | undefined;
  isKanbanLayout: boolean;
  isOpen: boolean;
  getTopOptions: () => any;
  getOptions: () => any;
  onUpdatePersonField: (
    field: string,
    value: any,
    additionalFields?: any
  ) => void;
  onUpdateDealField: (field: string, value: any) => void;
  onUpdateCompanyField: (field: string, value: any) => void;
  onUpdateLeadField: (field: string, value: any) => void;
  onUpdateContractField?: (field: string, value: any) => void;
  onUpdatePartnerField?: (field: string, value: any) => void;
  onUpdatePartnerContactField?: (
    field: string,
    value: string | number | boolean | string[],
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
}

const QuickViewTitle: React.FC<QuickViewTitleProps> = ({
  item,
  itemType,
  currentEntity,
  isKanbanLayout,
  isOpen,
  getTopOptions,
  getOptions,
  onUpdatePersonField,
  onUpdateDealField,
  onUpdateCompanyField,
  onUpdateLeadField,
  onUpdateContractField,
  onUpdatePartnerField,
  onUpdatePartnerContactField
}) => {
  const { t } = useTranslation();
  const stages = useDealStages().data?.stages;

  const { openDetailedView } = useEntityType(
    itemType,
    item,
    isKanbanLayout,
    undefined,
    undefined,
    isOpen
  );

  const displayName =
    itemType === EntityType.PERSON && currentEntity
      ? getFullName(currentEntity as PersonData)
      : itemType === EntityType.PARTNER_CONTACT && currentEntity
        ? getPartnerContactFullName(currentEntity as PartnerContactData)
        : currentEntity?.name || item?.name;

  const currentStage = useMemo(() => {
    return itemType === EntityType.DEAL
      ? find(stages, (stage) => stage.id === (currentEntity as DealData)?.stage)
      : null;
  }, [itemType, stages, currentEntity]);

  if (!item) return null;

  const handleNameSave = (newValue: string) => {
    if (itemType === EntityType.PERSON) {
      const [firstName, lastName] = newValue.split(" ");
      onUpdatePersonField("firstName", firstName, { lastName });
    } else if (itemType === EntityType.DEAL) {
      onUpdateDealField("name", newValue);
    } else if (itemType === EntityType.COMPANY) {
      onUpdateCompanyField("name", newValue);
    } else if (itemType === EntityType.LEAD) {
      onUpdateLeadField("name", newValue);
    } else if (itemType === EntityType.CONTRACT) {
      onUpdateContractField?.("name", newValue);
    } else if (itemType === EntityType.PARTNER) {
      onUpdatePartnerField?.("name", newValue);
    } else if (itemType === EntityType.PARTNER_CONTACT) {
      const [firstName = "", ...rest] = newValue
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      onUpdatePartnerContactField?.("firstName", firstName, {
        lastName: rest.join(" ")
      });
    }
  };

  return (
    <div className="flex w-full justify-between items-center gap-4">
      <div className="flex gap-2 items-center justify-start flex-1 min-w-0">
        <div className="min-w-0 flex-shrink overflow-hidden">
          <SingleInput
            fieldName="name"
            initialValue={displayName}
            onSave={handleNameSave}
            textVariant="lgSubtitleH1"
            inputVariant="lgSubtitleH1"
            className="pl-0 truncate"
          />
        </div>
        {!isEmpty(currentStage) && (
          <StageBadge stage={currentStage} data-test="current-stage" />
        )}
      </div>
      <div className="flex gap-2 items-center">
        <OptionsDropdownMenu
          options={getOptions()}
          topOptions={getTopOptions()}
          tPrefix="detailedView.options"
          iconSize="md"
        />
        <Tooltip>
          <TooltipTrigger data-test="open-detailed-view">
            <ClickableLink
              href={
                itemType === EntityType.DEAL && item?.id
                  ? getDealDetailedViewUrl(item.id)
                  : `/${entityTypeToEntityName[itemType]}/${item?.id}`
              }
              onClick={() => {
                openDetailedView();
              }}
              stopPropagation
            >
              <IconButton variant="icon" size="md">
                <ArrowUpRight />
              </IconButton>
            </ClickableLink>
          </TooltipTrigger>
          <TooltipContent>{t("detailedViewTooltip")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default QuickViewTitle;
